import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wrench, Cpu, MapPin, BadgeCheck, Truck, CarFront, Star, ChevronRight,
  CalendarCheck, FileText, MessageCircle, ShieldCheck, Zap, Users, Clock, Building2, Tag, Newspaper,
} from "lucide-react";
import { useLang } from "@/i18n";
import { waLink, GARAGE, API } from "@/config";

const HERO_IMG = "https://images.unsplash.com/photo-1640616111573-276dd3e31666?crop=entropy&cs=srgb&fm=jpg&q=85";
const DIAG_IMG = "https://images.unsplash.com/photo-1727893380169-4dda123e19f7?crop=entropy&cs=srgb&fm=jpg&q=85";

const cardIcons = [Wrench, Cpu, MapPin, BadgeCheck, Truck, CarFront, Tag];
const whyIcons = [Cpu, Users, ShieldCheck, Wrench, Zap, BadgeCheck, MessageCircle, Building2];

const reviews = [
  { name: "Jeroen V.", text: "Snelle diagnose en eerlijke prijs. Mijn auto liep dezelfde dag nog perfect. Aanrader!", rating: 5 },
  { name: "Sarah M.", text: "WhatsApp contact is super handig. Offerte gekregen binnen het uur en afspraak de dag erna.", rating: 5 },
  { name: "Karim B.", text: "Sleepdienst kwam snel ter plaatse en de reparatie was transparant en correct. Top service.", rating: 5 },
];

export default function Home() {
  const { t } = useLang();
  const cards = t("cards");
  const [googleReviews, setGoogleReviews] = useState(null);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && d.reviews?.length && setGoogleReviews(d))
      .catch(() => {});
  }, []);

  const shownReviews = googleReviews
    ? googleReviews.reviews.slice(0, 6).map((r) => ({
        name: r.author?.name || "Google",
        text: r.text,
        rating: r.rating || 5,
        time: r.relativeTime,
        photo: r.author?.photoUri,
      }))
    : reviews;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Milano Worx garage" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(10,10,12,0.96) 0%, rgba(10,10,12,0.8) 50%, rgba(10,10,12,0.45) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 30%, rgba(225,29,72,0.15) 0%, transparent 60%)" }} />
        <div className="container-x relative py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span data-testid="hero-badge" className="overline-red inline-block border border-[#E11D48]/40 bg-[#E11D48]/10 rounded-full px-4 py-1.5">
              {t("hero.badge")}
            </span>
            <h1 data-testid="hero-title" className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              {t("hero.title")}
            </h1>
            <p data-testid="hero-sub" className="mt-5 text-base sm:text-lg text-zinc-300 leading-relaxed">{t("hero.sub")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/afspraak" data-testid="hero-cta-appointment" className="btn-red"><CalendarCheck size={18} />{t("hero.cta1")}</Link>
              <Link to="/offerte" data-testid="hero-cta-quote" className="btn-outline"><FileText size={18} />{t("hero.cta2")}</Link>
              <a href={waLink(t("wa.general"))} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp" className="btn-wa">
                <MessageCircle size={18} />{t("hero.cta3")}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {[t("hero.p1"), t("hero.p2"), t("hero.p3")].map((p, i) => (
                <span key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                  <ShieldCheck size={15} className="text-[#E11D48]" />{p}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-16 sm:py-24" data-testid="services-section">
        <p className="overline-red">Milano Worx</p>
        <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white">{t("home.servicesTitle")}</h2>
        <p className="mt-3 text-zinc-400 max-w-2xl">{t("home.servicesSub")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c, i) => {
            const Icon = cardIcons[i];
            const to = c.id === "verhuur" ? "/verhuur" : c.id === "tekoop" ? "/te-koop" : `/diensten#${c.id}`;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                <Link to={to} data-testid={`service-card-${c.id}`}
                  className="card-dark p-6 block group hover:border-[#E11D48]/50 transition-colors duration-300 h-full">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#E11D48]/10 border border-[#E11D48]/30 flex items-center justify-center">
                      <Icon size={22} className="text-[#E11D48]" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#E11D48] border border-[#E11D48]/40 rounded-full px-2.5 py-1">{c.badge}</span>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold tracking-tight text-zinc-100 group-hover:text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#E11D48]">
                    {t("nav.diensten")} <ChevronRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#121215] border-y border-zinc-800/80" data-testid="how-section">
        <div className="container-x py-16 sm:py-24">
          <p className="overline-red">Milano Worx</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.howTitle")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t("home.steps").map((s, i) => (
              <div key={i} data-testid={`how-step-${i}`} className="card-dark p-6">
                <span className="font-mono2 text-4xl font-bold text-[#E11D48]/30">0{i + 1}</span>
                <h3 className="mt-3 text-lg font-semibold text-zinc-100">{s.t}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY + IMAGE */}
      <section className="container-x py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center" data-testid="why-section">
        <div className="relative rounded-2xl overflow-hidden border border-zinc-800">
          <img src={DIAG_IMG} alt="Diagnose" className="w-full h-80 lg:h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
        </div>
        <div>
          <p className="overline-red">Milano Worx</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.whyTitle")}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t("home.whyItems").map((item, i) => {
              const Icon = whyIcons[i];
              return (
                <div key={i} data-testid={`why-item-${i}`} className="flex items-center gap-3 card-dark p-4">
                  <Icon size={18} className="text-[#E11D48] shrink-0" />
                  <span className="text-sm font-medium text-zinc-200">{item}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GARAGE PHOTO */}
      <section className="container-x py-16 sm:py-20" data-testid="garage-section">
        <p className="overline-red">Milano Worx</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.garageTitle")}</h2>
        <p className="mt-2 text-zinc-400">{t("home.garageSub")}</p>
        <div className="mt-8 rounded-2xl overflow-hidden border border-zinc-800">
          <img src="/garage.jpg" alt="Milano Worx garage Hoboken" className="w-full h-72 sm:h-[460px] object-cover object-center" data-testid="garage-photo" />
        </div>
      </section>

      {/* RENTAL TEASER */}
      <section className="bg-[#121215] border-y border-zinc-800/80" data-testid="rental-teaser">
        <div className="container-x py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <p className="overline-red">Milano Worx Rental</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.rentalTitle")}</h2>
            <p className="mt-3 text-zinc-400">{t("home.rentalSub")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/verhuur" data-testid="rental-teaser-cta" className="btn-red"><CarFront size={18} />{t("home.rentalCta")}</Link>
            <a href={waLink(t("wa.rental"))} target="_blank" rel="noreferrer" className="btn-wa"><MessageCircle size={18} />WhatsApp</a>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="container-x py-16 sm:py-24" data-testid="reviews-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="overline-red">Reviews</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.reviewsTitle")}</h2>
            <p className="mt-2 text-zinc-400">{t("home.reviewsSub")}</p>
          </div>
          <a href={googleReviews?.googleMapsUri || `https://www.google.com/maps/search/${encodeURIComponent(GARAGE.name + " " + GARAGE.address)}`} target="_blank" rel="noreferrer"
            data-testid="google-reviews-cta" className="btn-outline !px-4 !py-2.5 text-sm shrink-0">
            <Star size={15} />{t("home.reviewsCta")}
            {googleReviews && <span className="font-mono2 text-[#D97706] font-bold ml-1" data-testid="google-rating-badge">{googleReviews.rating}★ ({googleReviews.userRatingCount})</span>}
          </a>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {shownReviews.map((r, i) => (
            <div key={i} data-testid={`review-${i}`} className="card-dark p-6">
              <div className="flex items-center gap-3 mb-3">
                {r.photo && <img src={r.photo} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />}
                <div>
                  <div className="flex gap-1">
                    {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={14} className="fill-[#D97706] text-[#D97706]" />)}
                  </div>
                  {r.time && <span className="text-[10px] text-zinc-500">{r.time}</span>}
                </div>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">"{r.text}"</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-wider text-zinc-500">{r.name}{googleReviews && <span className="normal-case font-normal"> · Google</span>}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-zinc-600">{googleReviews ? "Google" : t("home.reviewsNote")}</p>
      </section>

      {/* PRESS */}
      <section className="bg-[#121215] border-y border-zinc-800/80" data-testid="press-section">
        <div className="container-x py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline-red">{t("press.title")}</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-white">{t("press.articleTitle")}</h2>
            <p className="mt-2 text-sm text-zinc-500">{t("press.sub")}</p>
            <blockquote className="mt-6 border-l-2 border-[#E11D48] pl-5 text-zinc-300 leading-relaxed italic" data-testid="press-quote">
              {t("press.quote")}
            </blockquote>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("press.source")}</p>
            <a href="https://www.antwerpen.be/nl/overzicht/district-hoboken-1/ontdek-hoboken/vakmanschap-bij-de-broers-van-milano-worx" target="_blank" rel="noreferrer" className="btn-outline mt-6" data-testid="press-article-link">
              <Newspaper size={17} />{t("press.cta")}
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-zinc-800 relative">
            <img src="/press.jpg" alt="Hicham en Ahmed van Milano Worx" className="w-full h-72 lg:h-[420px] object-cover" data-testid="press-photo" />
            <span className="absolute bottom-2 right-3 text-[10px] text-white/70 bg-black/50 rounded px-2 py-0.5">{t("press.credit")}</span>
          </div>
        </div>
      </section>

      {/* FLEET / B2B */}
      <section className="bg-[#121215] border-y border-zinc-800/80" data-testid="fleet-section">
        <div className="container-x py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline-red">B2B</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{t("home.fleetTitle")}</h2>
            <p className="mt-3 text-zinc-400 leading-relaxed">{t("home.fleetDesc")}</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {t("home.fleetItems").map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                  <Clock size={14} className="text-[#E11D48] shrink-0" />{f}
                </div>
              ))}
            </div>
          </div>
          <div className="card-dark p-8 text-center">
            <Building2 size={36} className="mx-auto text-[#E11D48]" />
            <a href={waLink(t("wa.quote"))} target="_blank" rel="noreferrer" data-testid="fleet-cta" className="btn-red mt-6 w-full">
              {t("home.fleetCta")}
            </a>
            <a href={`mailto:${GARAGE.email}`} className="mt-3 inline-block text-sm text-zinc-400 hover:text-white transition-colors">{GARAGE.email}</a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-x py-16 sm:py-20 text-center" data-testid="final-cta">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white">{t("home.ctaTitle")}</h2>
        <p className="mt-3 text-zinc-400">{t("home.ctaSub")}</p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/afspraak" className="btn-red" data-testid="final-cta-appointment"><CalendarCheck size={18} />{t("hero.cta1")}</Link>
          <Link to="/offerte" className="btn-outline" data-testid="final-cta-quote"><FileText size={18} />{t("hero.cta2")}</Link>
          <a href={waLink(t("wa.general"))} target="_blank" rel="noreferrer" className="btn-wa"><MessageCircle size={18} />WhatsApp</a>
        </div>
      </section>
    </div>
  );
}
